import { Campaign, CharacterWithSkills, PartyResponse, SkillProficiencies, ClassName, SkillBonusOverrides, SkillName } from '@shared/types';

// WHY: Backend routes are mounted under /api/*, so all frontend URLs must include /api
const API_BASE = import.meta.env.VITE_API_URL || 'https://dnd-party-api.creamies.lol';

// WHY: When the API throws an uncaught error, Cloudflare returns an HTML
// "Internal Server Error" page. A bare res.json() on that body crashes the app
// with "Unexpected token 'I'...". This helper reads the raw text, parses safely,
// and throws a readable message instead of a JSON parse error.
export async function parseJsonResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: any;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(
      `Server returned an invalid response (status ${res.status}). ` +
      `Body: ${text.slice(0, 200)}`
    );
  }
  if (!res.ok) {
    throw new Error(data?.error || `Request failed with status ${res.status}`);
  }
  return data as T;
}

export async function getCampaign(slug: string): Promise<{ campaign: Campaign; characters: any[] }> {
  const res = await fetch(`${API_BASE}/api/campaigns/${slug}`);
  return parseJsonResponse(res);
}

export async function updateCampaign(
  slug: string,
  data: { name?: string; level?: number }
): Promise<Pick<Campaign, 'id' | 'slug' | 'name' | 'level'>> {
  const res = await fetch(`${API_BASE}/api/campaigns/${slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return parseJsonResponse(res);
}

export async function createCharacter(
  slug: string,
  data: {
    name: string;
    class: ClassName;
    strength: number;
    dexterity: number;
    constitution: number;
    intelligence: number;
    wisdom: number;
    charisma: number;
    skills: SkillProficiencies;
    languages: string[];
    toolProficiencies: string[];
    skillBonusOverrides?: SkillBonusOverrides;
  }
): Promise<CharacterWithSkills> {
  const res = await fetch(`${API_BASE}/api/campaigns/${slug}/characters`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return parseJsonResponse<CharacterWithSkills>(res);
}

export async function getCharacter(id: string): Promise<CharacterWithSkills> {
  const res = await fetch(`${API_BASE}/api/characters/${id}`);
  return parseJsonResponse(res);
}

export async function updateCharacter(
  id: string,
  data: Partial<CharacterWithSkills & { skills: SkillProficiencies; skillBonusOverrides?: SkillBonusOverrides }>
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/characters/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await parseJsonResponse(res);
}

export async function getPartyData(
  slug: string
): Promise<PartyResponse> {
  const res = await fetch(`${API_BASE}/api/campaigns/${slug}/party`);
  return parseJsonResponse(res);
}

// Admin API
export async function adminCreateCampaign(
  password: string,
  name: string,
  level: number
): Promise<Campaign> {
  const res = await fetch(`${API_BASE}/api/admin/campaigns`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Password': password,
    },
    body: JSON.stringify({ name, level }),
  });
  return parseJsonResponse<Campaign>(res);
}

export async function adminUpdateCampaign(
  password: string,
  id: string,
  name: string,
  level: number
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/campaigns/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Password': password,
    },
    body: JSON.stringify({ name, level }),
  });
  await parseJsonResponse(res);
}

export async function adminDeleteCampaign(password: string, id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/campaigns/${id}`, {
    method: 'DELETE',
    headers: { 'X-Admin-Password': password },
  });
  await parseJsonResponse(res);
}

export async function adminGetCampaigns(password: string): Promise<Campaign[]> {
  const res = await fetch(`${API_BASE}/api/admin/campaigns`, {
    headers: { 'X-Admin-Password': password },
  });
  return parseJsonResponse(res);
}

export async function adminToggleCharacterExclude(
  password: string,
  characterId: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/characters/${characterId}/exclude`, {
    method: 'PUT',
    headers: { 'X-Admin-Password': password },
  });
  await parseJsonResponse(res);
}
